import React, { useState } from 'react';
import JsonView from 'react18-json-view';
import { copyToClipboard } from "../../../utils/copyToClipboard";


const detectContentType = (response) => {
  if (!response) return 'unknown';

  if (response.headers && response.headers['content-type']) {
    const contentType = response.headers['content-type'].toLowerCase();
    if (contentType.includes('application/json')) return 'json';
    if (contentType.includes('text/html')) return 'html';
    if (contentType.includes('application/xml') || contentType.includes('text/xml')) return 'xml';
    if (contentType.includes('text/plain')) return 'text';
    if (contentType.includes('application/javascript')) return 'javascript';
    if (contentType.includes('text/css')) return 'css';
  }
  
  if (response.originalResponse && response.originalResponse.headers) {
    const contentType = response.originalResponse.headers['content-type'] || response.originalResponse.headers['Content-Type'];
    if (contentType) {
      const lowerContentType = contentType.toLowerCase();
      if (lowerContentType.includes('application/json')) return 'json';
      if (lowerContentType.includes('text/html')) return 'html';
      if (lowerContentType.includes('application/xml') || lowerContentType.includes('text/xml')) return 'xml';
      if (lowerContentType.includes('text/plain')) return 'text';
      if (lowerContentType.includes('application/javascript')) return 'javascript';
      if (lowerContentType.includes('text/css')) return 'css';
    }
  }
  
  let actualData = response;
  if (response.data) {
    actualData = response.data;
  } else if (response.originalResponse) {
    if (response.originalResponse.data) {
      actualData = response.originalResponse.data;
    } else if (response.originalResponse.body) {
      actualData = response.originalResponse.body;
    } else {
      actualData = response.originalResponse;
    }
  } else if (response.body) {
    actualData = response.body;
  }
  
  if (typeof actualData === 'string') {
    const trimmedData = actualData.trim();
    
    if ((trimmedData.startsWith('{') && trimmedData.endsWith('}')) || 
        (trimmedData.startsWith('[') && trimmedData.endsWith(']'))) {
      try {
        JSON.parse(trimmedData);
        return 'json';
      } catch (e) {}
    }
    
    if (trimmedData.startsWith('<!DOCTYPE html>') || 
        (trimmedData.startsWith('<html') && trimmedData.includes('</html>'))) {
      return 'html';
    }
    
    if (trimmedData.startsWith('<?xml') || 
        (trimmedData.startsWith('<') && trimmedData.includes('</') && !trimmedData.includes('<html'))) {
      return 'xml';
    }
    
    return 'text';
  }
  
  if (response.data) {
    const data = response.data;
    if (typeof data === 'string') {
      if (data.trim().startsWith('<') && data.includes('>')) {
        return data.trim().startsWith('<?xml') ? 'xml' : 'html';
      }
      return 'text';
    }
    if (typeof data === 'object') return 'json';
  }
  
  if (response.originalResponse && response.originalResponse.data) {
    const data = response.originalResponse.data;
    if (typeof data === 'string') {
      if (data.trim().startsWith('<') && data.includes('>')) {
        return data.trim().startsWith('<?xml') ? 'xml' : 'html';
      }
      return 'text';
    }
    if (typeof data === 'object') return 'json';
  }
  
  if (typeof response === 'string') {
    if (response.trim().startsWith('<') && response.includes('>')) {
      return response.trim().startsWith('<?xml') ? 'xml' : 'html';
    }
    return 'text';
  }
  
  if (typeof response === 'object') return 'json';
  
  return 'unknown';
};


const JsonPreview = ({ data }) => {
  let jsonData = data;
  if (typeof data === 'string') {
    try {
      jsonData = JSON.parse(data);
    } catch (e) {
      return <div className="text-red-500 text-sm">Invalid JSON format</div>;
    }
  }

  return (
    <JsonView
      editable={true}
      enableClipboard={true}
      src={jsonData}
      style={{ backgroundColor: "#f8f9fa" }}
    />
  );
};


const HtmlPreview = ({ data }) => (
  <div className="border border-gray-300 bg-white">
    <div 
      className="p-4 max-h-96 overflow-auto"
      style={{ fontFamily: 'system-ui, -apple-system, sans-serif', lineHeight: '1.6' }}
      dangerouslySetInnerHTML={{ 
        __html: `
          <style>
            h1 { font-size: 2rem; font-weight: bold; margin: 0 0 1rem 0; }
            h2 { font-size: 1.5rem; font-weight: bold; margin: 0 0 0.75rem 0; }
            h3 { font-size: 1.25rem; font-weight: bold; margin: 0 0 0.5rem 0; }
            h4 { font-size: 1.125rem; font-weight: bold; margin: 0 0 0.5rem 0; }
            h5 { font-size: 1rem; font-weight: bold; margin: 0 0 0.25rem 0; }
            h6 { font-size: 0.875rem; font-weight: bold; margin: 0 0 0.25rem 0; }
            p { margin: 0 0 1rem 0; }
            ul, ol { margin: 0 0 1rem 0; padding-left: 1.5rem; }
            li { margin: 0.25rem 0; }
            blockquote { margin: 1rem 0; padding: 0.5rem 1rem; border-left: 4px solid #e5e7eb; background: #f9fafb; }
            code { background: #f3f4f6; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: 'Courier New', monospace; }
            pre { background: #f3f4f6; padding: 1rem; border-radius: 0.25rem; overflow-x: auto; }
            a { color: #3b82f6; text-decoration: underline; }
            strong, b { font-weight: bold; }
            em, i { font-style: italic; }
            img { max-width: 100%; height: auto; }
            table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
            th, td { border: 1px solid #d1d5db; padding: 0.5rem; text-align: left; }
            th { background: #f9fafb; font-weight: bold; }
          </style>
          ${data}
        `
      }}
    />
  </div>
);


const XmlPreview = ({ data }) => (
  <pre className="bg-gray-900 text-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
    {data}
  </pre>
);


const TextPreview = ({ data }) => (
  <pre className="bg-white border border-gray-300 p-4 rounded text-sm overflow-auto max-h-96 whitespace-pre-wrap">
    {data}
  </pre>
);


const ResponseRenderer = ({ response, isError = false, className = '' }) => {
  const [activeTab, setActiveTab] = useState('preview');
  const [copied, setCopied] = useState(false);

  if (!response) {
    return (
      <div className={`p-4 text-center text-gray-500 ${className}`}>
        No response data available
      </div>
    );
  }

  let data = response;
  if (response.data) {
    data = response.data;
  } else if (response.originalResponse) {
    if (response.originalResponse.data) {
      data = response.originalResponse.data;
    } else if (response.originalResponse.body) {
      data = response.originalResponse.body;
    } else {
      data = response.originalResponse;
    }
  } else if (response.body) {
    data = response.body;
  }

  const contentType = detectContentType(response);

  const formatRawData = () => {
    if (typeof data === 'string') return data;
    return JSON.stringify(data, null, 2);
  };

  const handleCopy = () => {
    copyToClipboard(formatRawData());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderPreview = () => {
    switch (contentType) {
      case 'json':
        return <JsonPreview data={data} />;
      case 'html':
        return <HtmlPreview data={data} />;
      case 'xml':
        return <XmlPreview data={data} />;
      case 'text':
      case 'javascript':
      case 'css':
        return <TextPreview data={data} />;
      default:
        return <TextPreview data={data} />;
    }
  };

  return (
    <div className={`response-renderer ${className}`}>
      <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 border-b">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
          }`}>
            {isError ? 'ERROR' : 'SUCCESS'}
          </span>
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
            {contentType.toUpperCase()}
          </span>
          <span className="text-sm text-gray-600">
            {typeof data === 'string' ? `${data.length} characters` : 'Object'}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className={`px-3 py-1 text-xs transition-colors cursor-pointer ${
            copied
              ? 'bg-green-600 text-white'
              : 'bg-gray-600 text-white hover:bg-gray-700'
          }`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('raw')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'raw'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Raw
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'preview'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Preview
        </button>
      </div>

      <div className="mt-4">
        {activeTab === 'raw' && (
          <div className="bg-gray-50 border border-gray-200">
            <pre className="p-4 text-sm overflow-auto max-h-96 whitespace-pre-wrap font-mono">
              {formatRawData()}
            </pre>
          </div>
        )}
        
        {activeTab === 'preview' && (
          <div className="bg-white border border-gray-200">
            <div className="p-4">
              {renderPreview()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponseRenderer;